import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { AuthenticationService } from '@app-auth/services/authentication.service';
import { EulaConsentService } from '@app-auth/services/eula-consent/eula-consent.service';
import {
  INTERNAL_ROUTES,
  FEATURE_ANNOUNCEMENT_VERSION,
  ROUTER_LINK_PAGE_NAME_MAP,
  PORTAL_RELEASE_VERSION,
  MIXPANEL_INIT_KEY_QA,
  MIXPANEL_INIT_KEY_PROD,
} from '@app-core/constants/constants';
import { KEYBOARD_SHORTCUTS } from '@app-core/constants/keyboard-shortcuts.constants';
import { LoginFleetInfo, PreferencesResponse, RenderableLogo, Theme } from '@app-core/models/core.model';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { DialogService } from '@app-core/services/dialog/dialog.service';
import { GoogleTagManagerService, LogoutTypes, ToggleState } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { KeyboardShortcutsService } from '@app-core/services/keyboard-shortcuts/keyboard-shortcuts.service';
import { StorageService } from '@app-core/services/storage/storage.service';
import { UpdateSideNavigationConfig } from '@app-shared/actions/sidenavigation-config.action';
import { UpdateUserPermission } from '@app-shared/actions/user-permission.actions';
import { State } from '@app-shared/reducers/sidenavigation-config.reducer';
import { getClientConfig } from '@config/client-config';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { CookieConsentComponent } from '../cookie-consent/cookie-consent.component';
import { FeatureAnnouncementComponent } from '../feature-announcement/feature-announcement.component';
import { PortalSearchComponent } from '../portal-search/portal-search.component';
import { ShortcutsListComponent } from '../shortcuts-list/shortcuts-list.component';
import { TwoFactorAuthenticationComponent } from '../two-factor-authentication/two-factor-authentication.component';
import { UserSettingsComponent } from '../user-settings/user-settings.component';
import { CookieService } from '@app-core/services/cookie/cookie.service';
import { AuthGuard } from '@app-core/guards/auth/auth.guard';
import mixpanel from 'mixpanel-browser';
import { environment } from '@env/environment';
import { NotificationComponent } from '@app-shared/components/notification-component/notification-component.component';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { SuprsendService } from '../suprsend-notification/suprsend.service';
import { UtilityService } from '@app-shared/services/common/utility.service';
import { resolveAsset } from '@app-shared/services/asset-resolver/asset-resolver.service';
import { BrandingService } from '@app-core/services/branding/branding.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() currentTheme: string = 'light';
  @Input() currentLanguage: string;
  @Input() public isTouchDevice = false;
  @Input() public isMobileView = false;

  public clientConfig: any;
  public portalReleaseVersion = PORTAL_RELEASE_VERSION;
  public currentTimeZone = '';
  public isReadableDateFormat;
  public currentMetricUnit = '';
  public currentDateFormat = '';
  public currentFleetId = '';
  public userFleetList = [];
  public currentRouteName: string;
  public navigationRoutes: string[] = [];
  public config: any;
  public loginName = '';
  public loginType: string;
  public userProfileIcon: string;
  public isLogout = false;
  public isInternalRoute = false;
  public isMobile = false;
  public isTripDetails = false;
  public fleetManagerName: string;
  public currentOS = 'windows';
  public keyboardShortcuts = KEYBOARD_SHORTCUTS;
  public NotificationComponent: Promise<Component | typeof NotificationComponent>;

  public isSideNavOpen = true;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public currentCookieConsent = null;
  public currentFleetInfo: LoginFleetInfo;
  private userId: string;
  public user_distinct_id: string;
  private routerSubscription!: Subscription;
  private currentFullUrl: string = '';
  private url: string = '';
  public showPilotModeChip: boolean = false;
  public daysRemaining: number;
  public formattedDate: string;
  public showDisabledBanner: boolean;
  public showForeverTooltip: boolean;
  public pilotModeTooltip: string;
  public user_subscriberId: string;
  public canShowNotifcationIcon: boolean = false;
  public headerLogos: RenderableLogo[] = [];
  private failedLogoUrls = new Set<string>();
  public logosReady: boolean = false;
  public brandingInitialized: boolean = false;
  private readonly RESELLER_LOGO_STYLES = {
    minWidth: '36px',
    minHeight: '36px',

    maxWidth: '150px',
    maxHeight: '40px',

    width: 'auto',
    height: 'auto',

    objectFit: 'contain',
    objectPosition: 'left center',
  };

  constructor(
    private accessService: AccessService,
    private dataService: DataService,
    private gtmService: GoogleTagManagerService,
    public router: Router,
    private store: Store<State>,
    private dialog: MatDialog,
    private storageService: StorageService,
    private translate: TranslateService,
    private authService: AuthenticationService,
    private breakpointObserver: BreakpointObserver,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private cookieService: CookieService,
    private authGuard: AuthGuard,
    private eulaConsentService: EulaConsentService,
    private dialogService: DialogService,
    public durationService: DurationRangeService,
    private mixpanelService: MixPanelService,
    private suprsendService: SuprsendService,
    private utilService: UtilityService,
    private cdRef: ChangeDetectorRef,
    private brandingService: BrandingService
  ) {}

  public ngOnInit() {
    // Subscribe to branding changes to rebuild header logos
    this.brandingService.brandingResolved$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((resolved) => {
      if (!resolved) {
        this.brandingInitialized = false;
        return;
      }
      void (async () => {
        const clientConfigData = getClientConfig();
        if (clientConfigData?.configuration) {
          this.clientConfig = clientConfigData.configuration;
          this.config = this.clientConfig;
        }

        this.clearLogoCache();
        this.failedLogoUrls.clear();
        void this.prepareHeaderLogos();

        this.brandingInitialized = true;
        this.cdRef.detectChanges();
      })();
    });

    // Initialize config first to ensure it's available for the template
    const clientConfigData = getClientConfig();
    if (clientConfigData && clientConfigData.configuration) {
      this.clientConfig = clientConfigData.configuration;
      this.config = this.clientConfig;
      this.navigationRoutes = [...(this.clientConfig.allowedRoutes || [])];
    } else {
      // Fallback to prevent errors if config isn't loaded yet
      this.clientConfig = { showHeader: false };
      this.config = this.clientConfig;
      this.navigationRoutes = [];
    }

    const { loginName = '', fleets = [], name = '', loginType = '', userId } = this.accessService.getLoginInfo();
    this.canShowNotifcationIcon = !this.durationService?.disableSuprsendNotification && loginType === 'fleetmanager';
    this.userId = userId;
    this.loginName = loginName;
    this.userFleetList = fleets;
    this.fleetManagerName = name;
    this.loginType = loginType;
    this.userProfileIcon = this.loginName.slice(0, 1);

    this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.currentFullUrl = event.url;
      this.url = this.currentFullUrl.includes('?') ? this.currentFullUrl.substr(0, this.currentFullUrl.indexOf('?')) : this.currentFullUrl;
      this.currentRouteName = ROUTER_LINK_PAGE_NAME_MAP[this.url];
      this.isInternalRoute = INTERNAL_ROUTES.includes(this.url);
    });

    this.currentOS = this.dataService.getCurrentOS();

    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        this.isMobile = result.matches;
        void this.prepareHeaderLogos();
      });

    this.dataService._currentTimeZone.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.currentTimeZone = value;
    });

    this.dataService._isReadableDateFormat.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: boolean) => {
      this.isReadableDateFormat = value;
    });

    this.dataService._currentMetricUnit.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.currentMetricUnit = value;
    });

    this.dataService._currentDateFormat.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.currentDateFormat = value;
    });

    this.translate.onLangChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.getPilotModeTooltip();
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentFleetId = value;
        this.currentFleetInfo = fleets.filter((x: any) => x.fleetId === this.currentFleetId)[0];

        this.store.dispatch(
          new UpdateUserPermission({
            currentFleet: this.currentFleetId,
            permissions: this.currentFleetInfo?.role?.permissions,
            uiConfigurations: this.currentFleetInfo?.role?.uiPermissions,
          })
        );

        const { url: fullURL = '' } = this.router || {};
        const url = fullURL.includes('?') ? fullURL.substr(0, fullURL.indexOf('?')) : fullURL;
        const primaryPath = url.split('/')[1];
        this.getFleetDetails();
        if (primaryPath) {
          const isAccessible = this.authGuard.isRouteAccessible(primaryPath);
          if (!isAccessible) {
            this.router.navigate(['home']);
            return;
          }
          const isLoggedIn = this.accessService.hasValidToken();
          if (isLoggedIn) {
            this.getdistinctId();
          }
        }
      }
    });

    this.isSideNavOpen = window.innerWidth <= 1440 ? false : true;
    this.store.dispatch(
      new UpdateSideNavigationConfig({
        currentWindowWidth: window.innerWidth,
        isSideNavOpen: this.isSideNavOpen,
      })
    );

    if (this.url === '/smdrivers/coaching-session') {
      this.closeSideNav();
    }
    this.configureKeyboardShortcuts();
    const isLoggedIn = this.accessService.hasValidToken();
    if (isLoggedIn) {
      this.getdistinctId();
    }
    void this.prepareHeaderLogos();
  }

  public ngAfterViewInit() {
    this.initializaMixedPanel();
    const showFeature = this.storageService.getStorageValue(FEATURE_ANNOUNCEMENT_VERSION);
    const showAnnouncementModel = this.currentLanguage !== 'en' && this.currentLanguage !== 'en-GB';
    if (showAnnouncementModel && showFeature === null && !this.isMobile && this.config.showFeatureAnnouncement) {
      setTimeout(() => {
        this.OpenFeatureAnnouncementModal();
        this.storageService.setStorageValue(FEATURE_ANNOUNCEMENT_VERSION, 'hide');
      }, 100);
    } else {
      if (
        (this.currentCookieConsent === null || this.currentCookieConsent?.cookieExpires < new Date().getTime()) &&
        this.loginType === 'fleetmanager'
      ) {
        this.openCookieConsent();
      }
    }
    if (this.currentCookieConsent?.statisticalCookies === false) {
      this.cookieService.deleteAllCookies();
    }

    // If the user is a fleet manager and the portal notification is enabled, show the notification component
    if (this.loginType === 'fleetmanager' && !this.durationService?.disableSuprsendNotification) {
      // Gets the main path as defined in the router config and checks if it matches the current path
      const { url: fullURL = '' } = this.router || {};
      const pathUrl = new URL(fullURL, window.location.origin);
      const isMainPath = this.router.config.map((path) => path.path).includes(pathUrl.pathname.replace('/', ''));
      // If the main path does not match the current path, hide the notification component in an iframe
      if (!this.config.showHeader && !isMainPath) {
        this.NotificationComponent = Promise.resolve(null);
      } else {
        this.NotificationComponent = import('@app-shared/components/notification-component/notification-component.component').then(
          (module) => module.NotificationComponent
        );
      }
    }
  }

  private async prepareHeaderLogos(): Promise<void> {
    if (!this.config?.showLogo) {
      this.headerLogos = [];
      this.logosReady = true;
      return;
    }

    this.logosReady = false;
    this.headerLogos = [];

    // ONLY currently visible logos
    const criticalLogos = this.getCriticalLogos();

    await Promise.allSettled(criticalLogos.map((url) => this.preloadLogo(url)));

    this.logosReady = true;
    this.buildHeaderLogos();
    this.cdRef.detectChanges();

    // preload probable next assets lazily
    const deferredLogos = this.getDeferredLogos();

    this.runWhenIdle(() => {
      deferredLogos.forEach((url) => {
        void this.preloadLogo(url);
      });
    });
  }

  private getCriticalLogos(): string[] {
    const urls = new Set<string>();

    // PRELOAD BOTH RESELLER THEMES
    const resellerLogoConfig = this.brandingService.getTagBrandingConfig()?.configurations?.logo;

    const resellerLight = this.getLogoUrl(resellerLogoConfig, 'main', 'light', this.isMobile);

    const resellerDark = this.getLogoUrl(resellerLogoConfig, 'main', 'dark', this.isMobile);

    if (resellerLight) {
      urls.add(resellerLight);
    }

    if (resellerDark) {
      urls.add(resellerDark);
    }

    // PARTNER LOGOS
    if (this.config?.showPartnerLogo) {
      const partnerConfig = this.brandingService.getOriginalConfig()?.configuration?.partnerLogo;

      const partnerLight = this.getLogoUrl(partnerConfig, 'partner', 'light', this.isMobile);

      const partnerDark = this.getLogoUrl(partnerConfig, 'partner', 'dark', this.isMobile);

      if (partnerLight) {
        urls.add(partnerLight);
      }

      if (partnerDark) {
        urls.add(partnerDark);
      }
    }

    return [...urls];
  }

  private getDeferredLogos(): string[] {
    const urls = new Set<string>();

    const oppositeTheme: Theme = this.currentTheme === 'light' ? 'dark' : 'light';

    // SAME DEVICE - OPPOSITE THEME
    const sameDeviceMain = this.getLogoUrl(
      this.brandingService.getTagBrandingConfig()?.configurations?.logo,
      'main',
      oppositeTheme,
      this.isMobile
    );

    if (sameDeviceMain) {
      urls.add(sameDeviceMain);
    }

    // PARTNER LOGOS
    if (this.config?.showPartnerLogo) {
      // SAME DEVICE PARTNER
      const sameDevicePartner = this.getLogoUrl(
        this.brandingService.getOriginalConfig()?.configuration?.partnerLogo,
        'partner',
        oppositeTheme,
        this.isMobile
      );

      if (sameDevicePartner) {
        urls.add(sameDevicePartner);
      }

      // MOBILE VARIANTS
      const mobileMainCurrentTheme = this.getLogoUrl(
        this.brandingService.getTagBrandingConfig()?.configurations?.logo,
        'main',
        this.currentTheme as Theme,
        true
      );

      const mobileMainOppositeTheme = this.getLogoUrl(
        this.brandingService.getTagBrandingConfig()?.configurations?.logo,
        'main',
        oppositeTheme,
        true
      );

      if (mobileMainCurrentTheme) {
        urls.add(mobileMainCurrentTheme);
      }

      if (mobileMainOppositeTheme) {
        urls.add(mobileMainOppositeTheme);
      }

      const mobilePartnerCurrentTheme = this.getLogoUrl(
        this.brandingService.getOriginalConfig()?.configuration?.partnerLogo,
        'partner',
        this.currentTheme as Theme,
        true
      );

      const mobilePartnerOppositeTheme = this.getLogoUrl(
        this.brandingService.getOriginalConfig()?.configuration?.partnerLogo,
        'partner',
        oppositeTheme,
        true
      );

      if (mobilePartnerCurrentTheme) {
        urls.add(mobilePartnerCurrentTheme);
      }

      if (mobilePartnerOppositeTheme) {
        urls.add(mobilePartnerOppositeTheme);
      }
    }

    return [...urls];
  }

  private runWhenIdle(callback: () => void): void {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback);
      return;
    }

    setTimeout(callback, 300);
  }

  public trackByLogo(_index: number, logo: RenderableLogo): string {
    return `${logo.logoType}-${logo.theme}-${logo.src}`;
  }

  private getLogoUrl(logoConfig: any, logoType: 'main' | 'partner', theme: Theme, isMobile: boolean): string | null {
    if (!logoConfig) return null;

    if (logoType === 'main') {
      let raw: string | undefined;

      if (isMobile) {
        // Try mobile logo first, fall back to desktop logo
        raw =
          theme === 'light' ? logoConfig.mobileModeLightLogo || logoConfig.lightLogo : logoConfig.mobileModeDarkLogo || logoConfig.darkLogo;
      } else {
        raw = theme === 'light' ? logoConfig.lightLogo : logoConfig.darkLogo;
      }

      return raw ? resolveAsset(raw) : null;
    }

    // Partner logo
    let raw: string | undefined;

    if (isMobile) {
      // Try mobile partner logo first, fall back to desktop partner logo
      raw =
        theme === 'light'
          ? logoConfig.partnerMobileModeLightLogo || logoConfig.partnerLightLogo
          : logoConfig.partnerMobileModeDarkLogo || logoConfig.partnerDarkLogo;
    } else {
      raw = theme === 'light' ? logoConfig.partnerLightLogo : logoConfig.partnerDarkLogo;
    }

    return raw ? resolveAsset(raw) : null;
  }

  private preloadLogo(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (!url || this.failedLogoUrls.has(url)) {
        resolve(false);
        return;
      }

      const img = new Image();

      img.decoding = 'async';

      img.onload = () => {
        resolve(true);
      };

      img.onerror = () => {
        this.failedLogoUrls.add(url);
        resolve(false);
      };

      img.src = url;
    });
  }

  private getOriginalLogoStyles(logoType: 'main' | 'partner', isMobile: boolean): { width: string | null; height: string | null } {
    const original = this.brandingService.getOriginalConfig()?.configuration;

    if (logoType === 'main') {
      const logo = original?.logo;
      if (!logo) return { width: null, height: null };

      if (isMobile) {
        // Try mobile dimensions first, fall back to desktop dimensions
        return {
          width: logo.mobileModeWidth ?? logo.width ?? null,
          height: logo.mobileModeHeight ?? logo.height ?? null,
        };
      }

      return {
        width: logo.width ?? null,
        height: logo.height ?? null,
      };
    }

    const partner = original?.partnerLogo;
    if (!partner) return { width: null, height: null };

    if (isMobile) {
      // Try mobile dimensions first, fall back to desktop dimensions
      return {
        width: partner.mobileModeWidth ?? partner.width ?? null,
        height: partner.mobileModeHeight ?? partner.height ?? null,
      };
    }

    return {
      width: partner.width ?? null,
      height: partner.height ?? null,
    };
  }

  private resolveRenderableLogo(logoType: 'main' | 'partner', isMobile: boolean): RenderableLogo | null {
    const currentTheme = this.currentTheme as Theme;
    const oppositeTheme: Theme = currentTheme === 'light' ? 'dark' : 'light';

    const resellerConfig = logoType === 'main' ? this.brandingService.getTagBrandingConfig()?.configurations?.logo : null;

    const originalConfig =
      logoType === 'main'
        ? this.brandingService.getOriginalConfig()?.configuration?.logo
        : this.brandingService.getOriginalConfig()?.configuration?.partnerLogo;

    const candidateUrls = [
      { source: 'reseller-tag' as const, theme: currentTheme, url: this.getLogoUrl(resellerConfig, logoType, currentTheme, isMobile) },
      { source: 'reseller-tag' as const, theme: oppositeTheme, url: this.getLogoUrl(resellerConfig, logoType, oppositeTheme, isMobile) },
      { source: 'original' as const, theme: currentTheme, url: this.getLogoUrl(originalConfig, logoType, currentTheme, isMobile) },
      { source: 'original' as const, theme: oppositeTheme, url: this.getLogoUrl(originalConfig, logoType, oppositeTheme, isMobile) },
    ].filter((item) => !!item.url);

    const selected = candidateUrls.find((item) => !this.failedLogoUrls.has(item.url!));

    if (!selected?.url) {
      return null;
    }

    const isOriginal = selected.source === 'original';

    return {
      src: selected.url,
      alt: logoType === 'main' ? this.config?.logo?.alt : this.config?.partnerLogo?.alt,
      brandingSource: selected.source,
      logoType,
      theme: selected.theme,
      isMobile,
      styles: isOriginal ? this.getOriginalLogoStyles(logoType, isMobile) : this.RESELLER_LOGO_STYLES,
      showDividerAfter: false, // Will be set correctly in buildHeaderLogos()
    };
  }

  private buildHeaderLogos(): void {
    if (!this.config?.showLogo || !this.logosReady) {
      this.headerLogos = [];
      return;
    }

    const logos: RenderableLogo[] = [];
    const isMobile = this.isMobile;

    const mainLogo = this.resolveRenderableLogo('main', isMobile);

    let partnerLogo: RenderableLogo | null = null;
    if (this.config?.showPartnerLogo) {
      partnerLogo = this.resolveRenderableLogo('partner', isMobile);
    }

    if (mainLogo?.src) {
      // Show divider only when a valid partner logo exists (works for both desktop and mobile)
      mainLogo.showDividerAfter = Boolean(partnerLogo?.src);
      logos.push(mainLogo);
    }

    if (partnerLogo?.src) {
      logos.push(partnerLogo);
    }

    this.headerLogos = logos;
  }

  public getFleetDetails() {
    this.dataService
      .getFleetDetails()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: PreferencesResponse) => {
          if (res?.pilotModeConfiguration?.isEnabled) {
            this.showPilotModeChip = res?.pilotModeConfiguration?.isEnabled;
            this.getExpiryDateAndDays(res?.pilotModeConfiguration?.expiryDateUTC);
            this.getPilotModeTooltip();
          } else {
            this.showPilotModeChip = false;
          }
          if (res?.pilotModeConfiguration?.isEnabled === true && !('expiryDateUTC' in res.pilotModeConfiguration)) {
            this.showForeverTooltip = true;
          }
        },
        error: () => {},
      });
  }
  public getPilotModeTooltip() {
    const label1 = this.translate.instant('Days Left');
    const label2 = this.translate.instant('Current Expiry Date');

    const value1 = this.daysRemaining;
    const value2 = this.formattedDate;
    let space;
    const gap = '\u2003\u2003\u2003\u2003\u2003\u2003\u2003\u2002';
    if (value1 > 10) {
      space = '\u2003\u2003\u2003\u2003';
    } else {
      space = '\u2003\u2003\u2003\u2002';
    }

    const header = `${label1}${space}${label2}`;
    const values = `${value1}${gap}${value2}`;

    const sectionOne = this.translate.instant('pilotModeExpiryInfoSectionOne');
    const sectionTwo = this.translate.instant('pilotModeExpiryInfoSectionTwo');
    this.pilotModeTooltip = `${header}\n${values}\n\n${sectionOne}\n\n${sectionTwo}`;
  }

  getExpiryDateAndDays(date) {
    const expiryIsoDate = date;
    const expiryDate = new Date(expiryIsoDate);
    const today = new Date();

    const timeDiff = expiryDate.getTime() - today.getTime();
    this.daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    const day = expiryDate.getDate().toString().padStart(2, '0');
    const month = (expiryDate.getMonth() + 1).toString().padStart(2, '0');
    const year = expiryDate.getFullYear();

    this.formattedDate = `${day}/${month}/${year}`;
  }
  private getdistinctId() {
    this.suprsendService
      .getsuprsendDistinctId()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res: any) => {
          this.user_distinct_id = res.distinct_id || '';
          this.user_subscriberId = res.subscriberId || '';
          this.notifyDistinctIdToCompanionApp(res.distinct_id);
        },
        (error) => {
          console.log('Error fetching distinct ID:', error);
          this.user_distinct_id = '';
          this.user_subscriberId = '';
        }
      );
  }

  public openNotificationSettings() {
    this.router.navigateByUrl('/notification-setting');
  }

  public openHelpDocumentation() {
    window.open(this.config.helpDocumentationURL, '_blank');
  }

  public profileClick() {
    this.gtmService.profileClick(this.currentFleetInfo.fleetId);
    this.mixpanelService.track('Profile Icon', {
      Info: this.currentFleetInfo.fleetId,
    });
  }

  public async changeUserFleet(fleetId = '') {
    if (fleetId) {
      // const previousPlan = localStorage.getItem('clientPlan'); // 👈 Store it before anything changes
      // console.log('Previous client plan:', previousPlan);
      const alreadyAcceptedEula = await this.eulaConsentService.getEULAconsent(fleetId);
      if (!alreadyAcceptedEula) {
        const dialogRef = this.dialogService.openEULADialog(fleetId);
        const acceptedEULA = await dialogRef.afterClosed().toPromise();
        if (!acceptedEULA) {
          this.authService.logout();
          return;
        }
      }

      this.accessService.currentFleet = fleetId;
      this.dataService.currentFleet = fleetId;
      await this.brandingService.onFleetChange(fleetId);
      this.durationService
        .getClientConfig()
        .pipe(take(1), takeUntil(this.ngUnsubscribe))
        .subscribe(
          (res) => {
            const newPlan = (res as any)?.data?.planDetails?.plan || null;
            const previousPlan = localStorage.getItem('clientPlan') || null;
            if (newPlan !== previousPlan) {
              newPlan ? localStorage.setItem('clientPlan', newPlan) : localStorage.removeItem('clientPlan');
              window.location.reload();
            }
          },
          () => {}
        );
      this.utilService.updateUserMetadata({
        preferredFleet: fleetId,
      });
      this.gtmService.switchAccount(fleetId);
      this.mixpanelService.track('Switch account', {
        'Fleet id': fleetId,
      });
    }
  }

  private notifyDistinctIdToCompanionApp(distinctId: string) {
    const data = {
      distinctid: distinctId,
    };

    const message = {
      type: 'notifying distinctid to companion app',
      data,
    };

    const w = window as any;

    if (typeof w?.Android?.notifyDistinctId === 'function') {
      w.Android.notifyDistinctId(JSON.stringify(message));
    } else if (typeof w?.webkit?.messageHandlers?.nativeLogin?.postMessage === 'function') {
      w.webkit.messageHandlers.nativeLogin.postMessage(message);
    }
  }

  public OpenFeatureAnnouncementModal() {
    this.gtmService.viewFeatureAnnouncementModal(ToggleState.show);
    this.mixpanelService.track('View Feature Announcement Model', {
      Status: 'Show',
    });
    const dialogRef = this.dialog.open(FeatureAnnouncementComponent, {
      position: { top: '24px' },
      autoFocus: false,
      panelClass: ['custom-dialog', 'mobile-modal'],
    });

    dialogRef.afterClosed().subscribe(() => {
      this.storageService.setStorageValue(FEATURE_ANNOUNCEMENT_VERSION, 'hide');
      this.gtmService.viewFeatureAnnouncementModal(ToggleState.hide);
      this.mixpanelService.track('View Feature Announcement Model', {
        Status: 'Hide',
      });
      if (
        (this.currentCookieConsent === null || this.currentCookieConsent?.cookieExpires < new Date().getTime()) &&
        this.loginType === 'fleetmanager'
      ) {
        this.openCookieConsent();
      }
    });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public toggleSideNav() {
    this.gtmService.toggleSideNavigation(this.isSideNavOpen ? ToggleState.hide : ToggleState.show);
    this.mixpanelService.track('Toggle Side navigation', {
      Status: this.isSideNavOpen ? 'Close' : 'Open',
    });
    this.isSideNavOpen = !this.isSideNavOpen;
    this.store.dispatch(
      new UpdateSideNavigationConfig({
        currentWindowWidth: window.innerWidth,
        isSideNavOpen: this.isSideNavOpen,
      })
    );
  }

  public privacyPolicy() {
    this.gtmService.privacyPolicy(this.currentFleetInfo.fleetId);
    this.mixpanelService.track('Privacy Policy', {
      FleetId: this.currentFleetInfo.fleetId,
    });
  }

  public changeTheme() {
    this.gtmService.swtchTheme(this.currentTheme === 'light' ? 'dark' : 'light');
    this.currentTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.mixpanelService.track('Switch Theme', {
      'Current Theme': this.currentTheme === 'light' ? 'light' : 'dark',
    });
    this.dataService.currentTheme = this.currentTheme;
    this.accessService.currentTheme = this.currentTheme;

    void this.prepareHeaderLogos();
    if (this.currentCookieConsent?.functionalCookies === false) {
      this.dataService.deleteFunctionalCookies();
    }
    this.utilService.updateUserMetadata({
      theme: this.currentTheme,
    });
  }

  public openPortalSearch() {
    this.gtmService.portalSearch(ToggleState.show);
    this.mixpanelService.track('Portal Search Modal', {
      Status: 'Show',
    });
    this.dialog.closeAll();
    const dialogRef = this.dialog.open(PortalSearchComponent, {
      width: '640px',
      height: '480px',
      position: {
        top: '24px',
      },
      panelClass: ['mobile-modal'],
    });

    dialogRef.afterClosed().subscribe(() => {
      this.gtmService.portalSearch(ToggleState.hide);
      this.mixpanelService.track('Portal Search Modal', {
        Status: 'Hide',
      });
    });
  }

  public openUserSettings() {
    this.dialog.closeAll();
    this.mixpanelService.track('Settings clicked');
    const dialogRef = this.dialog.open(UserSettingsComponent, {
      width: '480px',
      minHeight: '480px',
      position: {
        top: '24px',
      },
      panelClass: ['mobile-modal'],
      autoFocus: false,
      data: {
        currentDateFormat: this.currentDateFormat,
        currentMetricUnit: this.currentMetricUnit,
        currentTimeZone: this.currentTimeZone,
        isReadableDateFormat: this.isReadableDateFormat,
        currentLanguage: this.currentLanguage,
      },
    });

    dialogRef.afterClosed().subscribe((data: any) => {
      if (data) {
        const { timezone, metricUnit, dateFormat, isReadableDateFormat, language } = data || {};
        this.utilService.updateUserMetadata({
          timezone,
          metricUnit,
          dateFormat,
          isReadableDateFormat,
          language,
        });

        this.changeUserSettings({
          timezone,
          metricUnit,
          dateFormat,
          isReadableDateFormat,
          language,
        });
      }
    });
  }

  public openTwoFactorAuthentication() {
    this.dialog.closeAll();
    this.dialog.open(TwoFactorAuthenticationComponent, {
      width: '480px',
      minHeight: '300px',
      position: {
        top: '24px',
      },
      panelClass: ['mobile-modal'],
      autoFocus: false,
      disableClose: true,
    });
  }

  public openCookieConsent() {
    this.dialog.closeAll();
    this.dialog.open(CookieConsentComponent, {
      panelClass: ['incident-modal', 'mobile-modal'],
      width: '480px',
      minHeight: '300px',
      position: {
        top: '24px',
      },
      autoFocus: false,
      disableClose: true,
    });
  }

  public openChangePassword() {
    this.mixpanelService.track('Open Change Password');
    this.dialog.closeAll();
    this.dialog.open(ChangePasswordComponent, {
      width: '480px',
      minHeight: '300px',
      position: {
        top: '24px',
      },
      panelClass: ['mobile-modal'],
      autoFocus: false,
      disableClose: true,
    });
  }

  public configureKeyboardShortcuts() {
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.viewSettings[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.openUserSettings();
      });

    if (this.loginType === 'fleetmanager') {
      this.keyboardShortcutsService
        .addShortcut({ keys: this.keyboardShortcuts.notificationSettings[this.currentOS] })
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          this.router.navigate(['/notification-setting'], {});
        });
    }

    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.portalSearch[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.openPortalSearch();
      });

    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.switchTheme[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (this.config.showTheme) {
          this.changeTheme();
        }
      });

    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.toggleSideMenu[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.toggleSideNav();
      });

    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.viewKeyboardShortcuts[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.openShortcutsList();
      });

    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.logout[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (confirm(this.translate.instant('headerComponentLogoutConfirm'))) {
          this.logout();
        }
      });
  }

  public changeUserSettings(data: any) {
    const { timezone, metricUnit, dateFormat, isReadableDateFormat, language } = data || {};

    if (this.accessService.currentLanguage !== language) this.accessService.isLanguageUserOverridden = true;
    this.accessService.currentLanguage = language;
    this.dataService.currentLanguage = language;

    if (this.accessService.currentTimeZone !== timezone) this.accessService.isTimeZoneUserOverridden = true;
    this.accessService.currentTimeZone = timezone;
    this.dataService.currentTimeZone = timezone;

    if (this.accessService.currentMetricUnit !== metricUnit) this.accessService.isMetricUnitUserOverridden = true;
    this.accessService.currentMetricUnit = metricUnit;
    this.dataService.currentMetricUnit = metricUnit;

    if (this.accessService.currentDateFormat !== dateFormat) this.accessService.isDateFormatUserOverridden = true;
    this.accessService.currentDateFormat = dateFormat;
    this.dataService.currentDateFormat = dateFormat;

    this.accessService.isReadableDateFormat = isReadableDateFormat;
    this.dataService.isReadableDateFormat = isReadableDateFormat;

    if (this.currentCookieConsent?.functionalCookies === false) {
      this.dataService.deleteFunctionalCookies();
    }
  }

  public openShortcutsList() {
    this.gtmService.viewKeyboardShortcuts(ToggleState.show);
    this.mixpanelService.track('View Keyboard Shortcuts', {
      Status: 'Show',
    });
    this.dialog.closeAll();
    const dialogRef = this.dialog.open(ShortcutsListComponent, {
      width: '640px',
      minHeight: '640px',
      position: {
        top: '24px',
      },
      panelClass: ['mobile-modal'],
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.gtmService.viewKeyboardShortcuts(ToggleState.hide);
      this.mixpanelService.track('View Keyboard Shortcuts', {
        Status: 'Hide',
      });
    });
  }

  public logout() {
    this.isLogout = true;
    const { loginType = 'Unknown' } = this.accessService.getLoginInfo();
    this.gtmService.logout(loginType === 'master' ? LogoutTypes.master : LogoutTypes.fleetManager, this.currentFleetId);
    this.mixpanelService.track('Logout', {
      FleetId: this.currentFleetId,
    });
    this.authService.logout();
  }

  public closeSideNav() {
    this.isSideNavOpen = false;
    this.gtmService.toggleSideNavigation(this.isSideNavOpen ? ToggleState.hide : ToggleState.show);
    this.store.dispatch(
      new UpdateSideNavigationConfig({
        currentWindowWidth: window.innerWidth,
        isSideNavOpen: this.isSideNavOpen,
      })
    );
  }

  private initializaMixedPanel() {
    const MIXPANEL_INIT_KEY = environment?.env === 'prod' ? MIXPANEL_INIT_KEY_PROD : MIXPANEL_INIT_KEY_QA;
    mixpanel.init(MIXPANEL_INIT_KEY, {
      opt_out_tracking_by_default: false,
      api_host: 'https://api-eu.mixpanel.com',
    });
    mixpanel.identify(this.loginName);
    mixpanel.people.set({
      $name: name,
      $email: this.loginName,
      $cognitoid: this.userId,
    });

    this.dataService._currentCookiesConsent.subscribe((value: string) => {
      this.currentCookieConsent = value;
      if (this.currentCookieConsent && this.currentCookieConsent.statisticalCookies) {
        mixpanel.opt_in_tracking();
      } else {
        mixpanel.opt_out_tracking();
      }
    });
  }

  private clearLogoCache(): void {
    this.failedLogoUrls.clear();
  }
}
